/**
 * Redux store configuration
 * @author Maheen Nasir
 * @description Sets up the Redux store with initial reducer configuration
 */

import { configureStore, combineReducers } from "@reduxjs/toolkit";
import configReducer from "./slices/configSlice";
import userReducer, { type UserState } from "./slices/userSlice";
import courseReducer from "./slices/courseSlice";
import courseDetailsReducer from "./slices/courseDetailsSlice";
import notificationReducer from "./slices/notificationSlice";
import instituteReducer from "./slices/instituteSlice";
import applicationsReducer from "./slices/applicationsSlice";
import renewalCoursesReducer from "./slices/renewalCoursesSlice";
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import storage from "redux-persist/lib/storage";
import type { ConfigState } from "./slices/configSlice";
import filterModalReducer from "./slices/filterModalSlice";
import registerInstituteFormReducer from "./slices/registerInstituteFormSlice";
import courseDetailsFormReducer from "./slices/courseDetailsFormSlice";
import referbackReducer from "./slices/referbackSlice";
import sidebarReducer from "./slices/sidebarSlice";
import externalPortalReducer from "./slices/externalPortalSlice";
import type { InstituteState } from "./slices/instituteSlice";
import type { RenewalCoursesState } from "./slices/renewalCoursesSlice.types";
import type { RegisterInstituteFormState } from "./slices/registerInstituteFormSlice";
import type { CourseDetailsFormState } from "./slices/courseDetailsFormSlice";
import type { ReferbackState } from "./slices/referbackSlice";

/**
 * Configured Redux store instance
 * @description Creates and configures the Redux store with initial reducers
 */

const configPersistConfig = {
	key: "config",
	storage,
	whitelist: [
		"selectedMenuItem",
		"challanId",
		"challanAmount",
		"applicationStatus",
		"applicationId",
		"applicationUuid",
		"certifiedApplicationUuid",
		"selectedInstituteId",
		"selectedSessionId",
		"recentApplicationData",
	],
};

const institutePersistConfig = {
	key: "institute",
	storage,
	whitelist: ["registerInstituteMode"],
};

const renewalCoursesPersistConfig = {
	key: "renewalCourses",
	storage,
	whitelist: ["courses", "totalCoursesToAdd", "currentCourseIndex", "isRenewalMode"],
};

const registerInstituteFormPersistConfig = {
	key: "registerInstituteForm",
	storage,
	whitelist: [
		"formData",
		"currentStep",
		"currentSubStep",
		"maxReachedStep",
		"maxReachedSubStep",
		"lastSaved",
		"isDirty",
		"isSameAsPrincipal",
		"selectedLocationOptions",
		"selectedInstituteOption",
	],
};

const courseDetailsFormPersistConfig = {
	key: "courseDetailsForm",
	storage,
	whitelist: [
		"formData",
		"currentStep",
		"currentSubStep",
		"maxReachedStep",
		"maxReachedSubStep",
		"lastSaved",
		"isDirty",
	],
};

const referbackPersistConfig = {
	key: "referback",
	storage,
	whitelist: ["selectedCourse", "selectedApplicationCourseId", "completedCourses", "lastStepByApplicationUuid"],
};

const userPersistConfig = {
	key: "user",
	storage,
	whitelist: [
		"user",
		"token",
		"isAuthenticated",
		"expireTimer",
		"resetToken",
		"loginTime",
		"lastActivity",
		"sessionId",
	],
};

const rootReducer = combineReducers({
	config: persistReducer<ConfigState>(configPersistConfig, configReducer),
	user: persistReducer<UserState>(userPersistConfig, userReducer),
	course: courseReducer,
	courseDetails: courseDetailsReducer,
	courseDetailsForm: persistReducer<CourseDetailsFormState>(courseDetailsFormPersistConfig, courseDetailsFormReducer),
	notifications: notificationReducer,
	institute: persistReducer<InstituteState>(institutePersistConfig, instituteReducer),
	applications: applicationsReducer,
	renewalCourses: persistReducer<RenewalCoursesState>(renewalCoursesPersistConfig, renewalCoursesReducer),
	registerInstituteForm: persistReducer<RegisterInstituteFormState>(
		registerInstituteFormPersistConfig,
		registerInstituteFormReducer,
	),
	filterModal: filterModalReducer,
	referback: persistReducer<ReferbackState>(referbackPersistConfig, referbackReducer),
	sidebar: sidebarReducer,
	/** In-memory only: avoids redux-persist REHYDRATE overwriting embed intent after AuthGuard runs. */
	externalPortal: externalPortalReducer,
});

export const store = configureStore({
	reducer: rootReducer,
	middleware: getDefaultMiddleware =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
			},
		}),
});

export const persistor = persistStore(store);

/** Type definition for the root state */
export type RootState = ReturnType<typeof store.getState>;
/** Type definition for the dispatch function */
export type AppDispatch = typeof store.dispatch;
